/**
 * Shared retract-with-reason prompt for LR Direct brokers.
 * Used identically by the BID panel and the QUOTE view.
 */
import { useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, XCircle } from "lucide-react";

const PRESETS = [
  "Capacity no longer available",
  "Rate entered was incorrect",
  "Covered another load",
  "Timing / appointment no longer works",
];

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  /** "bid" | "quote" — only affects copy. */
  kind: "bid" | "quote";
  submitting?: boolean;
  onConfirm: (reason: string) => void;
}

export default function RetractReasonDialog({ open, onOpenChange, kind, submitting, onConfirm }: Props) {
  const [reason, setReason] = useState("");
  useEffect(() => { if (open) setReason(""); }, [open]);

  const label = kind === "quote" ? "quote" : "bid";
  const valid = reason.trim().length >= 3;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Retract your {label}?</DialogTitle>
          <DialogDescription>
            A short reason is required. The shipper will see that your {label} was retracted, along with
            your reason. You can submit a new {label} afterwards.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2">
          <div className="flex flex-wrap gap-1.5">
            {PRESETS.map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => setReason(p)}
                className="rounded-full border border-border px-2.5 py-1 text-[11px] text-muted-foreground hover:bg-muted"
              >
                {p}
              </button>
            ))}
          </div>
          <Textarea
            value={reason}
            onChange={(e) => setReason(e.target.value.slice(0, 300))}
            placeholder={`Why are you retracting this ${label}?`}
            className="min-h-[84px] resize-none text-sm"
          />
          <div className="text-right text-[10px] text-muted-foreground">{reason.length} / 300</div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>Cancel</Button>
          <Button
            variant="destructive"
            disabled={!valid || submitting}
            onClick={() => onConfirm(reason.trim())}
          >
            {submitting ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <XCircle className="mr-1.5 h-4 w-4" />}
            Retract {label}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
